/* eslint-disable @typescript-eslint/camelcase */
import { ConnectResult, TomoContext } from '../TomoContext';
import { useContext, useEffect, useState } from 'react';
import ProxyLocalStorage, {
  useProxyLocalStorage,
} from '../v2/ProxyLocalStorage';
import {
  ConnectType,
  TomoOptions,
  TomoProviderEventName,
} from '../v2/types/types';

async function getEthereumProvider() {
  return (await import('../v2/provider/EthereumProvider/EthereumProvider')).EthereumProvider;
}

async function getTonProvider() {
  return (await import('../v2/provider/TonProvider/TonProvider')).TonProvider;
}

async function getSolanaProvider() {
  return (await import('../v2/provider/SolanaProvider/SolanaProvider')).default;
}

async function getSuiProvider() {
  return (await import('../v2/provider/SuiProvider/SuiProvider')).default;
}


type TomoConnectOptions = {
  tonProof?: string;
  email?: string;
  workChain?: number;
};

interface Iprops {
  chain: string;
  options: TomoOptions;
  setProviders: (providers: Record<string, any>) => void;
  setConnectResult?: (result: ConnectResult) => void;
}

const useTomoConnect = ({ chain, options, setConnectResult }: Iprops) => {
  const [connected, setConnected] = useState(false);
  const { setProviders, providers, useEvmChains } = useContext(TomoContext);
  const storage = useProxyLocalStorage();
  const defaultChain = +useEvmChains[0] || 1;
  // const [evmAccounts, setEvmAccounts] = useState([]); // not in use right now

  const connect = async (connectOptions?: TomoConnectOptions) => {
    const { email, tonProof, workChain } = connectOptions || {};
    let res;
    try {
      if (chain === 'EVM') {
        const ethereum = new (await getEthereumProvider())({ injected: true, ...options });
        res = await ethereum.request({
          method: 'eth_requestAccounts',
          params: email ? [{ email }] : [],
        });
        if (res?.[0]) {
          setProviders(pre => ({ ...pre, ethereum }));
          ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [
              {
                chainId: '0x' + defaultChain.toString(16),
              },
            ],
          });
        }
      } else if (chain === 'TON') {
        const tomo_ton = new (await getTonProvider())({
          injected: true,
          ...options,
        });
        const connectParams: TomoConnectOptions = {};
        if (tonProof) connectParams.tonProof = tonProof;
        if (email) connectParams.email = email;
        if (workChain) connectParams.workChain = connectParams.workChain ?? 0;
        res = await tomo_ton?.connect(connectParams);
        res.address &&
          setProviders(pre => ({ ...pre, tomo_ton }));
        res.tonProof && setConnectResult({ result: res.tonProof });
      } else if (chain === 'SOL') {
        const solana = new (await getSolanaProvider())({
          injected: true,
          ...options,
        });

        res = await solana.connectWallet(
          email ? { email } : undefined
        );
        res.address &&
          setProviders(pre => ({ ...pre, tomo_sol: solana }));
      } else if (chain === 'SUI') {
        const tomo_sui = new (await getSuiProvider())({
          injected: true,
          ...options,
        });
        res = await tomo_sui.connectWallet(
          email ? { email } : undefined
        );
         res.address && setProviders(pre => ({ ...pre, tomo_sui }));
      }
      // tomo connect success ?
      if ((chain === 'EVM' && res[0]) || res.address) {
        setConnected(true);
        storage.set('connect_type', 'TOMO');
      }
      return res;
    } catch (err) {
      console.log('connect error', err);
    }
  };

  // TODO  add to other providers
  const disconnectEffect = () => {
    storage.set('connect_type', '');
    setConnected(false);
  };


  const tryRestoreConnection = async () => {
    const storage = new ProxyLocalStorage();
    const connectType = storage.get('connect_type');
    if (connectType === 'TOMO') {
      // reconnect
      if (chain === 'EVM' && !providers.ethereum) {
        console.log('injecting reconnecting tomo evm');
        const evmProvider = new (await getEthereumProvider())({
          injected: true,
          ...options,
        });

        setProviders(pre => ({ ...pre, ethereum: evmProvider }));
      }
      if (chain === 'TON' && !providers.tomo_ton) {
        // todo
        const tomo_ton = new (await getTonProvider())({ injected: true, ...options });
        setProviders(pre => ({ ...pre, tomo_ton }));
      }
      // sol reconnection
      if (chain === 'SOL' && !providers.tomo_sol) {
        const tomo_sol = new (await getSolanaProvider())({ injected: true, ...options });
        setProviders(pre => ({ ...pre, tomo_sol }));
      }
      // sui reconnection
      if (chain === 'SUI' && !providers.tomo_sui) {
        const tomo_sui = new (await getSuiProvider())({ injected: true, ...options });
        setProviders(pre => ({ ...pre, tomo_sui }));
      }
    }
  }

  /* reconnection check */
  useEffect(() => {
    tryRestoreConnection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, providers]);

  // ethereum side effect
  useEffect(() => {
    const storage = new ProxyLocalStorage();
    const connectType = storage.get('connect_type') as ConnectType;
    if (connectType === 'TOMO') {
      providers.ethereum?.on(
        TomoProviderEventName.TOMO_EVM_PROVIDER_DISCONNECTED,
        disconnectEffect
      );
      return () => {
        providers.ethereum?.removeAllListeners?.();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers?.ethereum]);

  // ton side effect
  useEffect(() => {
    const storage = new ProxyLocalStorage();
    const connectType = storage.get('connect_type') as ConnectType;
    if (connectType === 'TOMO' && chain === 'TON') {
      providers?.tomo_ton?.on?.(
        TomoProviderEventName.TOMO_TON_PROVIDER_DISCONNECTED,
        disconnectEffect
      );
      return () => {
        providers?.tomo_ton?.removeAllListeners?.();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers?.tomo_ton]);

  return { connect, connected };
};

export default useTomoConnect;
